import React from 'react';
import { NavLink } from 'react-router-dom';

import { c } from 'ttag';

import { SidebarAccount, SidebarContext, useAuthentication, useLocalState } from '@freelancelabs/components';
import {
    BriefCaseOutline,
    ChevronDoubleLeft,
    ChevronDoubleRight,
    LogoutOutline,
    MemoOutline,
    PeopleOutline,
    SettingsOutline,
    UserCircleOutline,
    UserSolid,
} from '@freelancelabs/icons';
import { Stage, getStage } from '@freelancelabs/shared';
import { clsx } from '@freelancelabs/utils';

interface Entry {
    to: string;
    label: string;
    icon: React.ReactNode;
}

interface Props {
    /** Name of the signed-in person, from `viewer`. */
    name: string;
    /** Their role on Freelance.com: « Administrateur » or « Collaborateur ». */
    roleName: string;
}

/**
 * First letter of the first two words. Falls back to the first two characters when
 * there is a single word, and to nothing at all rather than a stray letter when the
 * name is empty: `viewer` exposes `firstName` and `lastName` as nullable.
 */
const initialsOf = (name: string) => {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) {
        return '';
    }
    if (words.length === 1) {
        return words[0].slice(0, 2).toUpperCase();
    }
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

/**
 * Persistent navigation of the back-office, and the first consumer of the
 * `--admin-rail-*` tokens the admin theme declares. Dark, because that is what
 * distinguishes an internal console from the portal at a glance: someone working
 * in both all day should never have to read the header to know where they are.
 *
 * Two groups, and the second one is the honest part: an entry appears under
 * « À venir » until its screen exists. A link that leads to an empty page costs
 * more trust than an entry that says it is not ready. « Rôles » moves up to
 * Administration when its module lands.
 */
const Rail = ({ name, roleName }: Props) => {
    const { logout } = useAuthentication();
    /**
     * Le rail replie, et le choix se garde d'une visite a l'autre.
     *
     * Attention, MEME MECANIQUE QUE LE PORTAIL, jusqu'a la cle de stockage, qui
     * est la seule chose a differer : les deux applications tournent sur des
     * origines distinctes en production mais partagent `localhost` en
     * developpement, ou une cle commune ferait replier l'une en repliant
     * l'autre. Le nom dit donc de quel rail il s'agit.
     *
     * Il est RANGE et non tenu en memoire, a la difference de la largeur d'une
     * colonne (PCG-159) : replier son rail est une preference de poste de
     * travail, la largeur d'une colonne un reglage du moment.
     */
    const [isShrink, setIsShrink] = useLocalState(false, 'ui-bo-rail-shrink');
    // getStage() reads the hostname; in production it returns `prod`, and naming
    // the environment then would be noise. Elsewhere it is exactly what someone
    // needs to know before clicking.
    const stage = getStage();

    const available: Entry[] = [
        // PCG-141. It left « À venir » with the screen that fills it, and the three
        // configuration panels came off the users list at the same time: the entry and
        // the address of a gesture move together, or the rail keeps announcing as
        // upcoming a screen that people are already using through another door.
        { to: '/comptes', label: c('Back-office nav').t`Accounts`, icon: <BriefCaseOutline size={16} /> },
        { to: '/utilisateurs', label: c('Back-office nav').t`Users`, icon: <UserSolid size={16} /> },
        { to: '/roles', label: c('Back-office nav').t`Roles`, icon: <SettingsOutline size={16} /> },
        // A referential of the platform, like « Rôles » and unlike the dashboard
        // catalogue, which is the object of an account and lives on the account.
        {
            to: '/listes-de-partenaires',
            label: c('Back-office nav').t`Partner lists`,
            icon: <PeopleOutline size={16} />,
        },
        // PCG-337. It left « À venir » with the screen that fills it, like « Comptes »
        // before it: the audit log of every administration gesture, read-only.
        { to: '/journal', label: c('Back-office nav').t`Activity log`, icon: <MemoOutline size={16} /> },
    ];

    const upcoming: Entry[] = [];

    return (
        <aside
            className={clsx(
                'shrink-0 bg-rail text-rail-text flex flex-col sticky top-0 h-screen transition-[width]',
                isShrink ? 'w-rail-shrink' : 'w-rail'
            )}
        >
            <div className={clsx('border-b border-rail-hover', isShrink ? 'px-2 pt-5 pb-4' : 'px-4 pt-5 pb-4')}>
                {/* Le titre disparait au repli plutot que de se tronquer : sur
                    70 pixels il ne resterait que « Client P… », qui ne nomme
                    plus rien. Le bouton, lui, reste toujours atteignable. */}
                {!isShrink && (
                    <p className="font-semibold text-sm tracking-tight">
                        {c('Back-office').t`Client Portal · Back-office`}
                    </p>
                )}
                <button
                    type="button"
                    onClick={() => setIsShrink(!isShrink)}
                    aria-expanded={!isShrink}
                    aria-label={
                        isShrink ? c('Back-office nav').t`Expand the menu` : c('Back-office nav').t`Collapse the menu`
                    }
                    title={
                        isShrink ? c('Back-office nav').t`Expand the menu` : c('Back-office nav').t`Collapse the menu`
                    }
                    className={clsx(
                        'flex items-center justify-center rounded-md p-1.5 text-rail-muted hover:bg-rail-hover hover:text-rail-text',
                        isShrink ? 'mx-auto' : 'ml-auto -mt-6'
                    )}
                >
                    {isShrink ? <ChevronDoubleRight size={14} /> : <ChevronDoubleLeft size={14} />}
                </button>
                {stage !== Stage.PROD && (
                    <span className="mt-1.5 inline-block font-mono text-[11px] text-rail-muted border border-rail-hover border-l-2 border-l-admin-accent px-1.5 py-0.5 rounded-sm">
                        {/* Le libelle ne tiendrait pas sur 70 px : au repli, le stage seul. */}
                        {isShrink ? (
                            stage
                        ) : (
                            <>
                                {c('Back-office').t`Environment`} {stage}
                            </>
                        )}
                    </span>
                )}
            </div>

            <nav className="flex-1 px-2 py-3" aria-label={c('Back-office nav').t`Administration`}>
                {!isShrink && (
                    <p className="px-2 pt-2 pb-1.5 text-[10.5px] uppercase tracking-[0.09em] text-rail-muted">
                        {c('Back-office nav').t`Administration`}
                    </p>
                )}
                {available.map(({ to, label, icon }) => (
                    <NavLink
                        key={to}
                        to={to}
                        title={isShrink ? label : undefined}
                        aria-label={isShrink ? label : undefined}
                        className={({ isActive }) =>
                            clsx(
                                'flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm no-underline text-rail-text hover:bg-rail-hover',
                                isShrink && 'justify-center',
                                isActive && 'bg-rail-hover font-medium shadow-[inset_2px_0_0_var(--admin-accent)]'
                            )
                        }
                    >
                        {icon}
                        {!isShrink && label}
                    </NavLink>
                ))}

                {!isShrink && upcoming.length > 0 && (
                    <p className="px-2 pt-4 pb-1.5 text-[10.5px] uppercase tracking-[0.09em] text-rail-muted">
                        {c('Back-office nav').t`Coming soon`}
                    </p>
                )}
                {upcoming.map(({ to, label, icon }) => (
                    // A span and not a disabled link: a screen reader announces a link
                    // it can follow, and this one cannot be followed.
                    <span
                        key={to}
                        // Replie, l'entree porte son nom ET son etat : « bientot »
                        // disparait avec le libelle, donc le survol doit dire les
                        // deux, faute de quoi une icone grisee se lit comme un
                        // ecran casse plutot que comme un ecran a venir.
                        title={isShrink ? `${label} · ${c('Back-office nav').t`soon`}` : undefined}
                        aria-label={isShrink ? `${label} · ${c('Back-office nav').t`soon`}` : undefined}
                        className={clsx(
                            'flex items-center gap-2.5 px-2.5 py-2 text-sm text-rail-muted cursor-not-allowed',
                            isShrink && 'justify-center'
                        )}
                    >
                        {icon}
                        {!isShrink && (
                            <>
                                {label}
                                <span className="ml-auto font-mono text-[10px]">{c('Back-office nav').t`soon`}</span>
                            </>
                        )}
                    </span>
                ))}
            </nav>

            {/* Le MEME bloc que le portail, et non un second ecrit « comme lui ».
                Le back-office n'offrait AUCUN moyen de se deconnecter : le pied du
                rail affichait un nom et un role, et quitter sa session demandait
                de vider son navigateur.

                Attention, seules les COULEURS changent, par la variante `--dark`
                qui ne redefinit que des jetons. Recrire le bloc aurait donne deux
                idees du chevron, du menu et de l'anneau de focus, et c'est ce que
                ce depot paie ailleurs.

                `roleName` prend la place de l'e-mail du portail : c'est ce que ce
                rail disait deja de la personne, et c'est ce qu'un operateur a
                besoin de lire sur lui-meme ici. */}
            {/* Le contexte plutot qu'une classe : c'est LUI que `SidebarAccount`
                lit pour cesser de rendre le nom et le chevron. Poser la seule
                classe `--shrink` aurait style un bloc qui rend encore son texte,
                donc un nom ecrase sous l'avatar sur 70 pixels. */}
            <SidebarContext.Provider value={{ isShrink }}>
                <div className={clsx('border-t border-rail-hover', isShrink ? 'p-2' : 'p-3')}>
                    <SidebarAccount
                        className="sidebar-account--dark"
                        name={name}
                        email={roleName}
                        initials={initialsOf(name)}
                    >
                        {/* Attention, CONDITIONNEL, et c'est la meme regle que le
                        portail : sans `PUBLIC_ACCOUNT_URL` le lien ne se rend
                        pas, plutot que de mener a une page qui n'existe pas. La
                        variable est optionnelle par construction, un
                        environnement peut tres bien ne pas servir cette
                        application. */}
                        {/* `?from` nomme l'origine, et c'est ce qui rend juste le lien de
                        retour d'`account` (PCG-304) : sans lui, cette page ne peut pas
                        savoir d'ou quelqu'un vient, et son retour renverrait un
                        administrateur sur le portail client, qui n'est pas d'ou il
                        vient. */}
                        {import.meta.env.PUBLIC_ACCOUNT_URL && (
                            <a href={`${import.meta.env.PUBLIC_ACCOUNT_URL}/account?from=hub-admin`}>
                                <UserCircleOutline />
                                {c('Back-office nav').t`My account`}
                            </a>
                        )}
                        <hr />
                        <a
                            href="#logout"
                            onClick={(event) => {
                                event.preventDefault();
                                void logout();
                            }}
                        >
                            <LogoutOutline />
                            {c('Back-office nav').t`Logout`}
                        </a>
                    </SidebarAccount>
                </div>
            </SidebarContext.Provider>
        </aside>
    );
};

export default Rail;
